import LeaderboardOutlinedIcon from '@mui/icons-material/LeaderboardOutlined';
import { useCallback } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { ApiEntityStatistics } from '../../../../api/hexa/ApiEntityStatistics';
import { AVAILABLE_STATISTICS_ORIGINS } from '../../../../api/hexa/models/EntityStatisticsModels';
import StatisticsRankedList from '../StatisticsRankedList';
import StatisticsSection from '../StatisticsSection';
import TooltipLink, { API_DOCS_URLS } from '../TooltipLink';
import {
  DateRangeFilter,
  OriginFilter,
  ResultsCountFilter,
  TagsFilter,
} from '../StatisticsFilters';
import { useMostSearchedStats } from '../useMostSearchedStats';

interface NamesTabProps {
  onFetchError: (message: string) => void;
}

const NamesTab = ({ onFetchError }: NamesTabProps) => {
  const { t } = useTranslation();

  const fetchNames = useCallback(
    (params: Parameters<typeof ApiEntityStatistics.mostSearchedNames>[0]) =>
      ApiEntityStatistics.mostSearchedNames(params).then(
        (response) => response.most_searched
      ),
    []
  );

  const namesStats = useMostSearchedStats(fetchNames, onFetchError);

  return (
    <StatisticsSection
      title={t('entity_statistics.names_title')}
      subtitle={t('entity_statistics.names_subtitle')}
      subtitleTooltip={
        <Trans
          i18nKey="entity_statistics.names_subtitle_tooltip"
          components={{
            entitiesLink: <TooltipLink href={API_DOCS_URLS.entities} />,
            batchLink: <TooltipLink href={API_DOCS_URLS.entitiesBatch} />,
          }}
        />
      }
      icon={<LeaderboardOutlinedIcon />}
      filtersTitle={t('entity_statistics.filters')}
      onResetFilters={namesStats.resetFilters}
      isLoading={namesStats.isLoading}
      loadingText={t('entity_statistics.names_loading')}
      isEmpty={namesStats.results.length === 0}
      emptyText={t('entity_statistics.names_empty')}
      filters={
        <>
          <DateRangeFilter state={namesStats} />
          <ResultsCountFilter state={namesStats} />
          <TagsFilter state={namesStats} />
          <OriginFilter
            state={namesStats}
            options={AVAILABLE_STATISTICS_ORIGINS}
            labelKeyPrefix="entity_statistics.origin_"
          />
        </>
      }
    >
      <StatisticsRankedList
        valueLabel={t('entity_statistics.search_count')}
        entries={namesStats.results.map((entry, index) => ({
          id: `${entry.name}-${index}`,
          label: entry.name,
          value: entry.searched_count,
        }))}
      />
    </StatisticsSection>
  );
};

export default NamesTab;
